import * as React from 'react';
import {
  List,
  Datagrid,
  TextField,
  EmailField,
  FunctionField,
  EditButton,
  Confirm,
  useNotify,
  useListContext,
} from 'react-admin';
import { IconButton } from '../icon-button/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
// Karta menu z olowkiem zamiast MenuBook: "Edytuj menu" to akcja edycji, a sama
// ksiazka mowila tylko "menu" i obok "Edytuj lokalizacje" z olowkiem wygladala na
// podglad, nie na edycje. Wcześniejszy wybor MenuBook zamiast RestaurantMenu dalej
// obowiazuje o tyle, ze skrzyzowane sztucce zlewaly sie w 20 px w znak ✕.
import { MenuCardPencilIcon } from '../icons/MenuCardPencilIcon';
import { Tooltip, Box, Typography, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RestaurantService } from '../../services/restaurant/restaurantService';
import { CardActionIcons } from '../card-actions/CardActions';
import { EmptyBox } from '../empty-box/EmptyBox';
import { PageHeader } from '../page-header/PageHeader';
import * as Sentry from '@sentry/react';
import { isAdminRole, hasRole } from '../../utils/roles';
import { listSurfaceSx } from '../../core/theme/listStyles';
import { cardActionButtonSx } from '../../core/theme/buttonStyles';

//TODO: move styles to a separate file if possible

const LocationTableActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  const isAdmin = isAdminRole();

  return (
    <PageHeader
      stretchActionsOnMobile
      title={t('resources.locationsManagement')}
      subtitle={t('locationsManagement.subtitle')}
      actions={
        isAdmin && (
          <IconButton
            icon={<AddIcon />}
            label={t('locationsManagement.addLocation')}
            sx={isMobile ? cardActionButtonSx : undefined}
            onClick={() => navigate('/locationsManagement/create')}
          />
        )
      }
    />
  );
};

interface MobileLocationListProps {
  navigate: ReturnType<typeof useNavigate>;
  setConfirmOpen: (open: boolean) => void;
  setToDelete: (id: number | null) => void;
}

const MobileLocationList = ({ navigate, setConfirmOpen, setToDelete }: MobileLocationListProps) => {
  const { data, isLoading } = useListContext();
  const { t } = useTranslation();
  const isAdmin = isAdminRole();
  const canEdit = !hasRole('CUSTOMER');

  if (isLoading || !data) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {data.map((record: any) => (
        <Box
          key={record.id}
          sx={{
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-xs)',
            p: 'var(--space-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
          }}
        >
          {/* Top row: name + locationType + actions. Trzy ikony zamiast kebaba z
              bottom sheetem — akcje sa od razu widoczne, kosztem szerokosci nazwy,
              ktora i tak ma `noWrap`. */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>
                {record.name ?? ''}
              </Typography>
              <Typography
                noWrap
                sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
              >
                {record.locationType ?? ''}
              </Typography>
            </Box>
            {/* mt: -2px wyrownuje ikony do linii bazowej nazwy, bez rozpychania wiersza */}
            <Box sx={{ mt: '-2px' }}>
              <CardActionIcons
                actions={[
                  canEdit && {
                    key: 'edit',
                    label: t('locationsManagement.editLocation'),
                    icon: <EditOutlinedIcon fontSize="small" />,
                    onClick: () => navigate('/locationsManagement/' + record.id + '/edit'),
                  },
                  {
                    key: 'menu',
                    label: t('locationsManagement.editMenu'),
                    icon: <MenuCardPencilIcon fontSize="small" />,
                    onClick: () =>
                      navigate('/locationsManagement/' + record.id + '/items', {
                        state: { locationId: record.id, locationName: record.name },
                      }),
                  },
                  {
                    key: 'promotion',
                    label: t('locationsManagement.sendPromotion'),
                    icon: <CampaignOutlinedIcon fontSize="small" />,
                    onClick: () =>
                      navigate('/locationsManagement/' + record.id + '/promotion', {
                        state: { locationId: record.id, locationName: record.name },
                      }),
                  },
                  isAdmin && {
                    key: 'delete',
                    label: t('common.delete'),
                    icon: <DeleteOutlineIcon fontSize="small" />,
                    destructive: true,
                    onClick: () => {
                      setToDelete(record.id ?? null);
                      setConfirmOpen(true);
                    },
                  },
                ]}
              />
            </Box>
          </Box>

          {/* Middle row: email • phone */}
          <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
            {[record.email, record.phoneNumber].filter(Boolean).join(' • ')}
          </Typography>

          {/* Bottom row: address — akcje siedza w naglowku, wiec adres ma cala szerokosc */}
          <Box sx={{ pt: 'var(--space-2)', borderTop: '1px solid var(--color-hairline)' }}>
            <Typography
              noWrap
              sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}
            >
              {record.address ?? ''}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const LocationTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDelete, setToDelete] = React.useState<number | null>(null);
  const restaurantService = new RestaurantService();
  const notify = useNotify();
  const isMobile = useMediaQuery('(max-width:600px)');
  const currentRole = localStorage.getItem('ra_role');
  const isAdmin = currentRole === 'ADMIN' || currentRole === 'SUPER_ADMIN';
  const canEdit = currentRole !== 'CUSTOMER';

  return (
    <>
      <LocationTableActions />
      <List
        actions={false}
        perPage={25}
        empty={
          <EmptyBox
            title={t('locationsManagement.emptyBox.title')}
            description={t('locationsManagement.emptyBox.description')}
            resource="locationsManagement"
            showCreate={isAdmin}
          />
        }
        sx={listSurfaceSx({ isMobile })}
      >
        {isMobile ? (
          <MobileLocationList
            navigate={navigate}
            setConfirmOpen={setConfirmOpen}
            setToDelete={setToDelete}
          />
        ) : (
          <Datagrid
            bulkActionButtons={false}
            rowClick={false}
            sx={{
              '& .RaDatagrid-headerCell': {
                fontWeight: 700,
                letterSpacing: 0.2,
              },
            }}
          >
            <TextField source="id" label={t('locationsManagement.columns.id')} sortable={false} />
            <TextField
              source="locationType"
              label={t('locationsManagement.columns.locationType')}
              sortable={false}
            />
            <TextField source="name" label={t('locationsManagement.columns.name')} sortable={false} />
            <EmailField
              source="email"
              label={t('locationsManagement.columns.email')}
              sortable={false}
            />
            <TextField
              source="phoneNumber"
              label={t('locationsManagement.columns.phone')}
              sortable={false}
            />
            <TextField
              source="address"
              label={t('locationsManagement.columns.address')}
              sortable={false}
            />
            <FunctionField
              label=""
              sortable={false}
              render={(record) => (
                <>
                  {canEdit && (
                    <Tooltip title={t('locationsManagement.editLocation')}>
                      <span>
                        <EditButton
                          record={record}
                          resource="locationsManagement"
                          icon={<EditOutlinedIcon fontSize="small" />}
                          label=""
                          sx={{
                            backgroundColor: 'transparent',
                            color: 'var(--color-primary)',
                            padding: 'var(--space-2)',
                            minWidth: 'auto',
                            borderRadius: 'var(--radius-md)',
                            '& .MuiButton-startIcon': { margin: 0 },
                            '&:hover': { backgroundColor: 'var(--color-primary-soft)' },
                          }}
                        />
                      </span>
                    </Tooltip>
                  )}

                  <Tooltip title={t('locationsManagement.editMenu')}>
                    <span>
                      <IconButton
                        icon={<MenuCardPencilIcon fontSize="small" />}
                        color="var(--color-primary)"
                        onClick={() =>
                          navigate('/locationsManagement/' + record.id + '/items', {
                            state: { locationId: record.id, locationName: record.name },
                          })
                        }
                      />
                    </span>
                  </Tooltip>

                  <Tooltip title={t('locationsManagement.sendPromotion')}>
                    <span>
                      <IconButton
                        icon={<CampaignOutlinedIcon fontSize="small" />}
                        color="var(--color-primary)"
                        onClick={() =>
                          navigate('/locationsManagement/' + record.id + '/promotion', {
                            state: { locationId: record.id, locationName: record.name },
                          })
                        }
                      />
                    </span>
                  </Tooltip>

                  {isAdmin && (
                    <Tooltip title={t('locationsManagement.deleteLocation')}>
                      <span>
                        <IconButton
                          icon={<DeleteOutlineIcon fontSize="small" />}
                          color="var(--color-error)"
                          onClick={() => {
                            setConfirmOpen(true);
                            setToDelete(record.id);
                          }}
                        />
                      </span>
                    </Tooltip>
                  )}
                </>
              )}
            />
          </Datagrid>
        )}
      </List>
      <Confirm
        isOpen={confirmOpen}
        title={t('commissionManagement.confirmDeletion.confirmDeletionTitle')}
        content={t('commissionManagement.confirmDeletion.confirmDeletionBody')}
        confirm={t('common.delete')}
        cancel={t('common.cancel')}
        onConfirm={async () => {
          try {
            await restaurantService.deleteAgency(toDelete!);
            notify(t('common.saved'), { type: 'success' });
            setConfirmOpen(false);
            setToDelete(null);
          } catch (error: any) {
            notify(error.response.data.error, { type: 'error' });
            console.error(error.response.data.error);
            Sentry.captureException(error);
            setConfirmOpen(false);
            setToDelete(null);
          }
        }}
        onClose={() => {
          setConfirmOpen(false);
          setToDelete(null);
        }}
      />
    </>
  );
};
